import { useRef } from 'react';
import { BfiDataGrid, Column, BfiActionColumn } from '@apply/ui';
import { PaymentStatusBubble } from '../components/PaymentStatusBubble';
import { PaymentMethodBlock } from '../components/PaymentMethodBlock';
import { Payment, PaymentGridRow } from '@apply/api/src/api/PaymentApi';
import { applicationInsights } from '../../ApplicationInsights';
import CustomStore from 'devextreme/data/custom_store';
import { PaymentStatus } from '@apply/constants';
import { GridRowAction } from '@apply/ui/src/lib/grids/GridRowActionButton';
import { FilterPanel, FilterRow, Scrolling } from 'devextreme-react/data-grid';
import './PaymentTable.css';

interface PaymentTableParameters {
    customStore: CustomStore;
    set_editModalPayment: React.Dispatch<React.SetStateAction<Payment | undefined>>;
    set_refundModalPayment: React.Dispatch<React.SetStateAction<Payment | undefined>>;
}

export const PaymentTable: React.FC<PaymentTableParameters> = ({ customStore, set_editModalPayment, set_refundModalPayment }) => {
    // eslint-disable-next-line
    const editPayment = (e: any) => {
        set_editModalPayment(e.data);
    };

    const onViewDetails = (item: PaymentGridRow) => {
        set_editModalPayment(item);
    };

    const onRefund = (item: PaymentGridRow) => {
        set_refundModalPayment(item);
    };

    const onRefundDisableCheck = (item: PaymentGridRow) => {
        return item.paymentStatus !== PaymentStatus.Success;
    };

    const tableRowActions: GridRowAction<PaymentGridRow>[] = [
        { text: 'View Details', onActionClick: onViewDetails },
        { text: 'Refund', onActionClick: onRefund, checkToDisable: onRefundDisableCheck },
    ];

    // DevExtreme copies row heights onto the separate fixed-column overlay table
    // during its first layout pass. Under network latency the fixed action column
    // could (does) end up sized against stale heights (the likely culprit being the
    // card images) and drifts out of alignment.
    // Force repaints here to fix it, staggered so we also catch late-loading content.
    const resyncingUntil = useRef(0);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resyncFixedColumns = (e: any) => {
        const now = Date.now();
        if (now < resyncingUntil.current) return; // Ignore contentReady from our own repaint
        resyncingUntil.current = now + 2000;
        [150, 600, 1500].forEach((ms) => setTimeout(() => e.component?.updateDimensions(), ms));
    };
    return (
        <>
            <BfiDataGrid
                className='payment-grid'
                keyExpr='bfiNumber'
                dataStore={customStore}
                applicationInsights={applicationInsights}
                onCellDblClick={editPayment}
                onContentReady={resyncFixedColumns}
                remoteOperations={true}
                height='100%' // Forces internal scroll - to ensure fixed columns' cell heights/positions remain synced with their respective rows, force native, standard scrolling via the <Scrollable /> below
            >
                <Scrolling useNative={true} mode="standard" />
                <FilterRow visible={true} />
                <FilterPanel visible={true} />
                <Column
                    dataField='amount'
                    caption='Amount'
                    dataType='number'
                    format={{ type: 'currency', precision: 2 }}
                    width={120}
                />
                <Column
                    dataField='status'
                    caption='Status'
                    dataType='string'
                    width={160}
                    alignment='left'
                    allowFiltering={false}
                    cellRender={(
                        cellData: any // eslint-disable-line
                    ) => <PaymentStatusBubble {...cellData.data} />}
                />
                <Column
                    dataField='paymentMethod'
                    caption='Payment Method'
                    width={120}
                    alignment='left'
                    cellRender={(
                        cellData: any // eslint-disable-line
                    ) => <PaymentMethodBlock {...cellData.data} />}
                    dataType='string'
                />
                <Column
                    dataField='bfiNumber'
                    caption='BFI #'
                    dataType='string'
                    width={100}
                />
                <Column
                    dataField='memo'
                    caption='Description'
                    dataType='string'
                />
                <Column
                    dataField='email'
                    caption='Customer Email'
                    dataType='string'
                    width={200}
                />
                <Column
                    dataField='paymentDate'
                    caption='Payment Date'
                    dataType='date'
                    width={160}
                    alignment='center'
                />
                {BfiActionColumn({ tableRowActions })}
            </BfiDataGrid>
        </>
    );
};
